import React, { Fragment } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  get,
  isEmpty,
  startCase,
  upperCase,
  map,
  pickBy,
  getOr
} from 'lodash/fp';
import { Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { ReservationType } from '../../../interfaces/reservation';
import guestAddressLine from '../../../helpers/guestAddressLine';
import reservationAddressLine from '../../../helpers/reservationAddressLine';
import StatusLabels from '../../../constants/reservation/StatusLabels';
import ReservationOrder from './ReservationOrder';

const styles = StyleSheet.create({
  page: {
    fontSize: 11,
    padding: '0 20px 100px',
    margin: '20px 0 10px'
  },
  headerSection: {
    display: 'flex',
    flexDirection: 'row'
  },
  columnA: {
    display: 'flex',
    width: '50%'
  },
  columnB: {
    width: '50%',
    display: 'flex',
    marginLeft: 10,
    paddingLeft: 10
  },
  section: {
    marginBottom: 6,
    flexDirection: 'row'
  },
  label: {
    width: '35%',
    color: '#444',
    fontSize: 10,
    textAlign: 'left',
    marginRight: 20,
    display: 'inline',
    flexGrow: 1
  },
  ageLabelView: {
    color: '#444',
    marginLeft: 8,
    flexDirection: 'row'
  },
  ageLabel: {
    width: '40%',
    color: '#444',
    fontSize: 10,
    textAlign: 'left',
    display: 'inline',
    flexGrow: 1
  },
  ageBreakdown: {
    marginTop: -10,
    marginLeft: 105,
    marginBottom: 16,
    flexDirection: 'row',
    display: ''
  },
  ageValue: {
    width: '40%',
    fontSize: 10,
    textAlign: 'left',
    display: 'inline',
    flexGrow: 1
  },
  value: {
    width: '70%',
    textAlign: 'left',
    display: 'inline',
    flexGrow: 1
  },
  contactNumber: {
    fontSize: 11,
    marginBottom: 2
  },
  orderSection: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row'
  },
  footer: {
    position: 'absolute',
    fontSize: 10,
    bottom: 66,
    left: 20,
    right: 0,
    textAlign: 'left',
    color: 'grey'
  },
  pagination: {
    position: 'absolute',
    fontSize: 10,
    bottom: 66,
    left: 0,
    right: 20,
    textAlign: 'right',
    color: 'grey'
  }
});

const guestDetails = reservation => {
  if (!reservation) return {};
  const { guestId, guest } = reservation;
  if (guestId && guest) {
    return {
      fullName: `${guest.firstname} ${guest.lastname}`,
      homeless: guest.homeless,
      householdTotal: guest.household_total,
      homePhone: get('phone', guest) || '',
      workPhone: get('workphone', guest) || '',
      cellPhone: get('cellphone', guest) || '',
      email: get('email', guest) || '',
      address: guestAddressLine(guest, true)
    };
  }
  return {
    fullName: `${reservation.firstName} ${reservation.lastName}`,
    homeless: reservation.homeless,
    householdTotal: reservation.householdTotal,
    homePhone: reservation.homePhone,
    workPhone: reservation.workPhone,
    cellPhone: reservation.cellPhone,
    email: reservation.email,
    address: reservationAddressLine(reservation, true)
  };
};

const ReservationDetailsPdf = ({ data, guestFields, showLine1Address }) => {
  const {
    guestId = null,
    requestTime,
    volunteer,
    note,
    barcode,
    orders = []
  } = data;
  const {
    fullName,
    homeless,
    homePhone,
    workPhone,
    cellPhone,
    email,
    address
  } = guestDetails(data);

  const ageFields = pickBy(
    ({ name }) => name.substr(0, 3) === 'age',
    guestFields
  );
  const programs = map('name', get('programs', data));

  return (
    <Page size="A4" style={styles.page} wrap>
      <View style={styles.headerSection}>
        <View style={styles.columnA}>
          {guestId && (
            <View style={styles.section}>
              <Text style={styles.label}>Guest ID:</Text>
              <Text style={styles.value}>{guestId}</Text>
            </View>
          )}

          <View style={styles.section}>
            <Text style={styles.label}>Name:</Text>
            <Text style={styles.value}>{startCase(fullName)}</Text>
          </View>

          {guestId && (
            <Fragment>
              <View style={styles.section}>
                <Text style={styles.label}>
                  {get('household_total.label', guestFields)} (Guest):
                </Text>
                <Text style={styles.value}>
                  {get('guest.household_total', data)} person(s)
                </Text>
              </View>

              {!isEmpty(ageFields) && (
                <View style={styles.section}>
                  <Text style={styles.label}>Household Age Group:</Text>
                  <View style={styles.ageLabelView}>
                    <Text style={styles.ageLabel}>Age Group</Text>
                    <Text style={styles.ageLabel}># of Individual</Text>
                  </View>
                </View>
              )}
              {map(
                ({ name: ageField, label }) => (
                  <View style={styles.ageBreakdown} key={ageField}>
                    <Text style={styles.ageValue}>{label}</Text>
                    <Text style={styles.ageValue}>
                      {getOr('0', `guest.${ageField}`, data)}
                    </Text>
                  </View>
                ),
                ageFields
              )}
            </Fragment>
          )}

          <View style={styles.section}>
            <Text style={styles.label}>
              {get('household_total.label', guestFields)}:
            </Text>
            <Text style={styles.value}>{data.householdTotal} person(s)</Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.label}>Address:</Text>
            <View style={styles.value}>
              {homeless ? (
                <Text>Homeless</Text>
              ) : (
                <Fragment>
                  {showLine1Address && (
                    <Text style={{ marginBottom: 2 }}>{address.line1}</Text>
                  )}
                  <Text>{address.line2}</Text>
                </Fragment>
              )}
            </View>
          </View>

          {!!email && (
            <View style={styles.section}>
              <Text style={styles.label}>Email:</Text>
              <Text style={styles.value}>{email}</Text>
            </View>
          )}

          <View style={styles.section}>
            <Text style={styles.label}>Contact Number(s):</Text>
            <View style={{ ...styles.value }}>
              {!!homePhone && (
                <Text style={styles.contactNumber}>Home: {homePhone}</Text>
              )}
              {!!workPhone && (
                <Text style={styles.contactNumber}>Work: {workPhone}</Text>
              )}
              {!!cellPhone && (
                <Text style={styles.contactNumber}>Cell: {cellPhone}</Text>
              )}
            </View>
          </View>
        </View>

        <View style={styles.columnB}>
          <View style={styles.section}>
            <Text style={styles.label}>Reservation:</Text>
            <Text style={styles.value}>{data.id}</Text>
          </View>
          <View style={styles.section}>
            <Text style={styles.label}>Status:</Text>
            <Text style={styles.value}>
              {upperCase(StatusLabels[data.status])}
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.label}>Date:</Text>
            <Text style={styles.value}>
              {moment(requestTime).format('dddd, MMM DD, YYYY')}
            </Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.label}>Time:</Text>
            <Text style={styles.value}>{moment(requestTime).format('LT')}</Text>
          </View>
          {!!barcode && (
            <View style={styles.section}>
              <Text style={styles.label}>Barcode:</Text>
              <Text style={styles.value}>{barcode}</Text>
            </View>
          )}
          {!isEmpty(volunteer) && (
            <View style={styles.section}>
              <Text style={styles.label}>Volunteer:</Text>
              <Text style={styles.value}>
                {get('firstName', volunteer)} {get('lastName', volunteer)}
              </Text>
            </View>
          )}
          <View style={styles.section}>
            <Text style={styles.label}>Help Desired:</Text>
            <Text style={styles.value}>{programs.join(', ')}</Text>
          </View>
        </View>
      </View>

      {!!note && (
        <View style={styles.section}>
          <Text style={{ ...styles.label, width: '3%' }}>Notes:</Text>
          <Text style={{ ...styles.value, width: '97%' }}>{note}</Text>
        </View>
      )}

      {!isEmpty(orders) && (
        <Fragment>
          <Text style={{ textAlign: 'center', margin: 5 }}>Order Items:</Text>
          <View style={styles.orderSection}>
            {orders.map(each => (
              <ReservationOrder data={each} key={`order-${each.id}`} />
            ))}
          </View>
        </Fragment>
      )}
      <Text style={styles.footer} fixed>
        Reservation: {data.id}
      </Text>
      <Text
        style={styles.pagination}
        render={({ pageNumber, totalPages }) =>
          `Page ${pageNumber} of ${totalPages}`
        }
        fixed
      />
    </Page>
  );
};

ReservationDetailsPdf.propTypes = {
  data: ReservationType.isRequired,
  guestFields: PropTypes.shape({}),
  showLine1Address: PropTypes.bool.isRequired
};

ReservationDetailsPdf.defaultProps = {
  guestFields: null
};

export default ReservationDetailsPdf;
